import React, { useEffect } from 'react'

import type { LinkItemType, TopLinkItemType } from './types'

import XoxxFooter from "@assets/icons/xoxx-footer.svg?url"
import LightBrandLogo from '@assets/elements/easy-weddings-logo-light.svg?url'

import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { faFacebookF, faPinterest, faInstagram, faYoutube, faTiktok } from '@fortawesome/free-brands-svg-icons'
import { footerMenu } from './data'
import { getAsset, getCountry }  from '@helper/getConfigValues'
import type { CountryType } from '../../types'
import scss from './Footer.module.scss'
import { getLoginStatus } from '@api/getIsLoggedIn'

interface RenderLinksProps {
    menu: TopLinkItemType[],
    countryCode: CountryType,
    isLoggedIn: boolean
}

const getCurrentYear = () => {
    return new Date().getFullYear();
};

const RenderLink = (item: LinkItemType, isLoggedIn: boolean) => {    
    let url = item.url;
    if(isLoggedIn && !!item.loggedInUrl) {
        url = item.loggedInUrl;
    }
    return (!!url ? <a href={url}>{item.title}</a> : item.title)
}

const RenderLinks = ({ menu, countryCode, isLoggedIn }: RenderLinksProps) => {
    return (
        <>
            {menu.map((item, index) => {
                if (!!item.country && item.country !== countryCode)
                {
                    return null;
                }
                return (
                    <div className={"col-lg col-6"} key={index}>
                        <div className={scss.linksTitle}>
                            {RenderLink(item, isLoggedIn)}
                        </div>
                        <ul className={scss.links}>
                            {!!item.submenu && item.submenu.map((subItem, count) => (!subItem.country || subItem.country == countryCode) && <li key={count}>{RenderLink(subItem, isLoggedIn)}</li>)}
                        </ul>
                    </div>
                )
            })}   
        </>
    )        
}

const Footer = () => {
    const [isLoggedIn, setIsLoggedIn] = React.useState<boolean>(false);

    useEffect(() => {
        getLoginStatus()
          .then(result => !!result && setIsLoggedIn(result.IsLoggedIn));
      }, []);

    const country = getCountry();
    const isAU = (country === 'au');
    const badgeHeight = 45;
    const badgeWidth = 152;

    return (
        <footer className={`container-fluid ${scss.footerContainer}`}>
            <div className="container-xxl">
                <div className="row">
                    <RenderLinks menu={footerMenu} countryCode={country} isLoggedIn={isLoggedIn} />
                    {!isAU && <div className={`col-6 col-lg-3 text-center`}>
                        <div className={scss.xoxx}>
                            <img loading="lazy" alt="Xoxx footer image" src={XoxxFooter} width="170" height="170"/>
                        </div>
                    </div>}
                    <div className="col col-lg-4">
                        <div className={scss.linksTitle}>Download the Easy Weddings app</div>
                        <div className="row mb-lg-5">
                            <div className="col-6 col-sm-auto">
                            {isAU ?
                                <a href="https://apps.apple.com/au/app/wedding-planner-easy-weddings/id433611621">
                                    <img loading="lazy" title="iOS app store link" alt="iOS app store link" className={scss["app-btn"]} src={getAsset('badges/ios-app-store.png')} height={badgeHeight} width={badgeWidth} />
                                </a>
                                :
                                <a href="https://apps.apple.com/gb/app/easy-wedding-planner-uk/id1449439781">
                                    <img loading="lazy"  title="iOS app store link" alt="iOS app store link" className={scss["app-btn"]} src={getAsset('badges/ios-app-store.png')} height={badgeHeight} width={badgeWidth} />
                                </a>
                            }
                            </div>
                            <div className="col-6 col-sm-auto">
                            {isAU ? 
                                <a href="https://play.google.com/store/apps/details?id=au.com.easyweddings.directory">
                                    <img loading="lazy" title="Android play store link" alt="Android play store link" className={scss["app-btn"]} src={getAsset('badges/google-play-store.png')} height={badgeHeight} width={badgeWidth} />
                                </a>
                                :
                                <a href="https://play.google.com/store/apps/details?id=au.com.easyweddings.directory.uk">
                                    <img loading="lazy" title="Android play store link" alt="Android play store link" className={scss["app-btn"]} src={getAsset('badges/google-play-store.png')} height={badgeHeight} width={badgeWidth} />
                                </a>
                            }
                            </div>
                        </div>
                        <div>
                            <span className={scss.vendorTitle}>Are you a wedding vendor?</span>
                            <span>Join {isAU ? "Australia" : "the UK"}'s largest and most trusted wedding destination today and connect with more couples to book more weddings.</span>
                            <div className={`${scss.linksTitle} mt-4`}>
                                <a href='/business/'>List your business<Icon icon={faArrowRight} size="sm" width={20} /></a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`row mt-5`}>
                    <div className="col-lg-2 col-6">
                        <div className={scss["footer-logo"]}>
                            <img loading="lazy" src={LightBrandLogo} alt="Easyweddings logo" className={scss.logo} width="166" height="31"/>
                        </div> 
                    </div>
                    <div className="col-lg-7">
                        <div className={`text-start ${scss.disclaimer}`}>
                            <span className={scss.companyName}>© {getCurrentYear()} Easy Weddings Pty Ltd.</span>
                            <span className={scss.dot}>&#183;</span><a href='/sitemap/'>Sitemap</a>
                            <span className={scss.dot}>&#183;</span><a href='/privacy/'>Privacy</a>
                            { isAU ? <><span className={scss.dot}>&#183;</span><a href='/business/legal/'>Legal</a></> : '' }
                        </div>
                    </div>
                    <div className="col-lg-3">
                        <div className={`d-flex gap-4 mb-4 ${scss["social-icon-container"]}`}>
                            <a className={scss["social-icon"]} href="https://www.facebook.com/EasyWeddings/" target="_blank" rel="noopener" role="link" aria-label="Easy Weddings Facebook">
                                <Icon icon={faFacebookF} size="lg" />
                            </a>
                            <a className={scss["social-icon"]} href="https://www.tiktok.com/@easyweddings/" target="_blank" rel="noopener" role="link" aria-label="Easy Weddings TikTok">
                                <Icon icon={faTiktok} size="lg" />
                            </a>
                            <a className={scss["social-icon"]} href="https://www.instagram.com/easyweddings/" target="_blank" rel="noopener" role="link" aria-label="Easy Weddings Instagram">
                                <Icon icon={faInstagram} size="lg" />
                            </a>
                            <a className={scss["social-icon"]} href="https://www.pinterest.com.au/easyweddings/" target="_blank" rel="noopener" role="link" aria-label="Easy Weddings Pinterest">
                                <Icon icon={faPinterest} size="lg" />
                            </a>
                            <a className={scss["social-icon"]} href="https://www.youtube.com/user/easyweddings/" target="_blank" rel="noopener" role="link" aria-label="Easy Weddings Youtube">
                                <Icon icon={faYoutube} size="lg" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default Footer
